import { useState, useEffect, useMemo, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Flow, SubFlow } from '../types';
import { NodeIdContext } from '../../context/NodeIdContext';
import { useGetNextStep } from '../useGetNextStep';
import {
    useGetLightApplySkipDataLazyQuery,
    GetLightApplySkipDataQuery,
    GetLightApplySkipDataQueryVariables,
} from './__generated__/useLightApplyFlow.generated';

// Type for GraphQL @include variables (extracted from generated query variables)
type IncludeVariables = Omit<GetLightApplySkipDataQueryVariables, 'nodeId'>;

/** Light Apply has no sub-flow detours; shared so the inferred return type is SubFlow[]. */
const NO_SUB_FLOWS: SubFlow[] = [];

/** Stub flow returned when this hook is not active */
const INACTIVE_STUB = {
    flow: {
        id: 'light-apply',
        name: 'Light Apply Flow',
        description: 'Streamlined multi-step application experience',
        steps: [],
    } as Flow,
    subFlows: NO_SUB_FLOWS,
    getNextStep: () => Promise.resolve(null),
    loading: false,
};

/**
 * Hook to get the Light Apply flow configuration with dynamic skip evaluation
 * @param isActive - Whether this flow is currently active. If false, returns a minimal stub.
 * @returns Object containing flow definition and dynamic getNextStep function
 */
export const useLightApplyFlow = (isActive: boolean = true) => {
    // Get current step from location (avoiding circular dependency with usePageFlow)
    const location = useLocation();
    const currentStep = location.pathname;

    // Get nodeId from context
    const nodeId = useContext(NodeIdContext);

    // Light Apply Flow Definition with proper GraphQL typing
    // Defined inside hook so canSkip functions can access hook data in the future
    const flow = useMemo(
        (): Flow<GetLightApplySkipDataQuery> =>
            isActive
                ? {
                      id: 'light-apply',
                      name: 'Light Apply Flow',
                      description: 'Streamlined multi-step application experience',
                      steps: [
                          // Contact Info Steps with Skip Logic
                          // Step 1: Name Input (基本情報)
                          {
                              routes: ['/light-apply/contact-info/1'],
                              label: '基本情報', // Name Input
                              skipOnBack: false,
                              canSkip: (data) => {
                                  // Skip if we already have name from draft
                                  const state = data?.smartApplyApplicationState;
                                  if (state?.__typename !== 'SmartApplyApplicationState')
                                      return false;

                                  const hasName =
                                      state.contactInfo?.firstName && state.contactInfo?.lastName;
                                  return !!hasName;
                              },
                          },
                          // Step 2: Contact Details (連絡先詳細)
                          {
                              routes: ['/light-apply/contact-info/2'],
                              label: '連絡先詳細', // Contact Details
                              skipOnBack: false,
                              canSkip: (data) => {
                                  // Skip if we already have email and phone from draft
                                  const state = data?.smartApplyApplicationState;
                                  if (state?.__typename !== 'SmartApplyApplicationState')
                                      return false;

                                  const hasContact =
                                      state.contactInfo?.email && state.contactInfo?.phoneNumber;
                                  return !!hasContact;
                              },
                          },
                          // Step 3: Date of Birth (生年月日)
                          {
                              routes: ['/light-apply/contact-info/3'],
                              label: '生年月日', // Date of Birth
                              skipOnBack: false,
                              canSkip: (data) => {
                                  // Skip if we already have date of birth from draft
                                  const state = data?.smartApplyApplicationState;
                                  if (state?.__typename !== 'SmartApplyApplicationState')
                                      return false;

                                  const hasDateOfBirth = state.contactInfo?.dateOfBirth;
                                  return !!hasDateOfBirth;
                              },
                          },
                          // Step 4: Gender (性別)
                          {
                              routes: ['/light-apply/contact-info/4'],
                              label: '性別', // Gender
                              skipOnBack: false,
                              canSkip: (data) => {
                                  // Skip if we already have gender from draft
                                  const state = data?.smartApplyApplicationState;
                                  if (state?.__typename !== 'SmartApplyApplicationState')
                                      return false;

                                  const hasGender = state.contactInfo?.gender;
                                  return !!hasGender;
                              },
                          },
                          // Step 5: Location (住所入力)
                          {
                              routes: ['/light-apply/contact-info/5'],
                              label: '住所入力', // Location/Address Input
                              skipOnBack: false,
                              canSkip: (data) => {
                                  // Skip if we already have address info from draft
                                  const state = data?.smartApplyApplicationState;
                                  if (state?.__typename !== 'SmartApplyApplicationState')
                                      return false;

                                  const hasAddress =
                                      state.contactInfo?.area &&
                                      state.contactInfo?.city &&
                                      state.contactInfo?.country;
                                  return !!hasAddress;
                              },
                          },
                          // Main Flow Steps
                          {
                              routes: ['/light-apply/resume'],
                              label: '履歴書', // Resume
                              skipOnBack: false,
                          },
                          {
                              routes: ['/light-apply/questions'],
                              label: '質問', // Screener Questions
                              skipOnBack: false,
                          },
                          {
                              routes: ['/light-apply/scout-setting'],
                              label: 'スカウト設定', // Scout Setting
                              skipOnBack: false,
                          },
                          {
                              routes: ['/light-apply/review'],
                              label: '確認・送信', // Review & Submit
                              skipOnBack: false,
                          },
                          {
                              routes: ['/light-apply/post-apply'],
                              label: '完了', // Complete
                              skipOnBack: true, // Users don't navigate back to success page
                          },
                      ],
                  }
                : INACTIVE_STUB.flow,
        [isActive],
    );

    // State for storing calculated include variables
    const [includeVariables, setIncludeVariables] = useState<IncludeVariables>({
        includeNames: true,
        includeGender: true,
        includeLocation: true,
        includeDateOfBirth: true,
        includeContact: true,
    });

    // Lazy query for dynamic skip evaluation
    const [fetchSkipData, { loading }] = useGetLightApplySkipDataLazyQuery({
        errorPolicy: 'all',
        // Depending on the skip logic, we may need to fetch the data from the server using fetchPolicy: 'network-only'
        fetchPolicy: 'cache-first',
    });

    // Helper to get contact info steps from flow (memoized since it depends on the flow)
    const contactInfoSteps = useMemo(
        () =>
            flow.steps.filter((step) =>
                step.routes.some((route) => route.startsWith('/light-apply/contact-info/')),
            ),
        [flow.steps],
    );

    // Update include variables when current step changes
    useEffect(() => {
        // Skip effect logic when inactive (performance optimization)
        if (!isActive) {
            return;
        }

        const currentRoute = currentStep || '';

        let newIncludeVars: IncludeVariables;

        if (!currentRoute || !currentRoute.startsWith('/light-apply/contact-info/')) {
            // For non-contact-info steps, include all fields as fallback
            newIncludeVars = {
                includeNames: true,
                includeGender: true,
                includeLocation: true,
                includeDateOfBirth: true,
                includeContact: true,
            };
        } else {
            // Find current step index
            const currentStepIndex = contactInfoSteps.findIndex((step) =>
                step.routes.includes(currentRoute),
            );

            if (currentStepIndex === -1) {
                // Fallback: include all fields if step not found
                newIncludeVars = {
                    includeNames: true,
                    includeGender: true,
                    includeLocation: true,
                    includeDateOfBirth: true,
                    includeContact: true,
                };
            } else {
                // Get remaining steps (after current step)
                const remainingSteps = contactInfoSteps.slice(currentStepIndex + 1);

                // Map step routes to their data categories
                const stepDataMap: Record<string, string[]> = {
                    '/light-apply/contact-info/1': ['includeNames'],
                    '/light-apply/contact-info/2': ['includeContact'],
                    '/light-apply/contact-info/3': ['includeDateOfBirth'],
                    '/light-apply/contact-info/4': ['includeGender'],
                    '/light-apply/contact-info/5': ['includeLocation'],
                };

                // Set include directives to true for all remaining steps
                const includeVars: IncludeVariables = {
                    includeNames: false,
                    includeGender: false,
                    includeLocation: false,
                    includeDateOfBirth: false,
                    includeContact: false,
                };

                // Enable includes for all data needed by remaining steps
                remainingSteps.forEach((step) => {
                    step.routes.forEach((route) => {
                        const dataCategories = stepDataMap[route] || [];
                        dataCategories.forEach((category: string) => {
                            if (category in includeVars) {
                                includeVars[category as keyof IncludeVariables] = true;
                            }
                        });
                    });
                });

                newIncludeVars = includeVars;
            }
        }

        setIncludeVariables(newIncludeVars);
    }, [isActive, currentStep, contactInfoSteps]);

    // Use the generic getNextStep hook for dynamic skip evaluation
    const { getNextStep, loading: getNextStepLoading } = useGetNextStep<
        GetLightApplySkipDataQueryVariables,
        GetLightApplySkipDataQuery
    >(flow, fetchSkipData, includeVariables, nodeId || null, false);

    if (!isActive) {
        return INACTIVE_STUB;
    }

    return {
        flow,
        subFlows: NO_SUB_FLOWS,
        getNextStep,
        loading: loading || getNextStepLoading,
    };
};
